import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import { VentLogo } from "./VentLogo";
import { getSocialMediaLinks } from "./SocialMediaSettings";
import { openExternalUrl } from "@/hooks/useExternalLink";

const XIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>;

const TherapistFooter = () => {
  const currentYear = new Date().getFullYear();
  const [socialLinks, setSocialLinks] = useState(getSocialMediaLinks());

  useEffect(() => {
    const updateLinks = () => setSocialLinks(getSocialMediaLinks());
    
    // Listen for storage changes (cross-tab) and custom event (same-tab)
    window.addEventListener('storage', updateLinks);
    window.addEventListener('social-media-updated', updateLinks);
    
    return () => {
      window.removeEventListener('storage', updateLinks);
      window.removeEventListener('social-media-updated', updateLinks);
    };
  }, []);

  return <footer className="bg-card border-t border-border mt-auto">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <VentLogo />
            <p className="text-muted-foreground text-sm">A safe space designed to help you let it out.</p>
          </div>

          {/* Company Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">Company</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                  About
                </Link>
              </li>
              <li>
                <Link to="/therapist-faq" className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                  FAQ
                </Link>
              </li>
              <li>
                <button 
                  onClick={() => openExternalUrl("mailto:support@theventapp.com")} 
                  className="text-muted-foreground hover:text-foreground transition-colors text-sm text-left"
                >
                  Contact Us
                </button>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/therapist-privacy" className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/therapist-terms" className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                  Terms of Use
                </Link>
              </li>
              <li>
                <Link to="/subscription-terms" className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                  Subscription Terms
                </Link>
              </li>
              <li>
                <Link to="/provider-agreement" className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                  Provider Agreement
                </Link>
              </li>
              <li>
                <Link to="/dmca" className="text-muted-foreground hover:text-foreground transition-colors text-sm">
                  DMCA
                </Link>
              </li>
            </ul>
          </div>

          {/* Social Media */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">Follow Us</h3>
            <div className="flex gap-4">
              <button 
                onClick={() => openExternalUrl(socialLinks.facebook)}
                className="text-muted-foreground hover:text-foreground transition-colors" 
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </button>
              <button 
                onClick={() => openExternalUrl(socialLinks.instagram)}
                className="text-muted-foreground hover:text-foreground transition-colors" 
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </button>
              <button 
                onClick={() => openExternalUrl(socialLinks.x)}
                className="text-muted-foreground hover:text-foreground transition-colors" 
                aria-label="X"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-border">
          <p className="text-center text-muted-foreground text-sm">
            © {currentYear} Vent. All rights reserved.
          </p>
        </div>
      </div>
    </footer>;
};

export default TherapistFooter;
